import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Fade,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  Cancel,
  Edit,
  Save,
  // Delete,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useSkills } from 'admin/hooks/useSkills';

interface Skill {
  id: string;
  name: string;
  isPublic: boolean;
}

const SkillsSettings = () => {
  const {
    getSkills,
    createSkill,
    updateSkill,
    //  deleteSkill
  } = useSkills();
  const [editableSkills, setEditableSkills] = useState<Skill[]>([]);
  const [originalSkills, setOriginalSkills] = useState<Skill[]>([]);
  const [newSkillName, setNewSkillName] = useState('');
  const [newSkillIsPublic, setNewSkillIsPublic] = useState(true);
  const [editingSkillId, setEditingSkillId] = useState<string | null>(null);

  useEffect(() => {
    if (getSkills.data) {
      setEditableSkills(getSkills.data);
      setOriginalSkills(getSkills.data);
    }
  }, [getSkills.data]);

  const handleSkillChange = (
    index: number,
    field: 'name' | 'isPublic',
    value: string | boolean
  ) => {
    const updated = [...editableSkills];
    updated[index] = { ...updated[index], [field]: value };
    setEditableSkills(updated);
  };

  const handleEdit = (skillId: string) => {
    setEditingSkillId(skillId);
  };

  const handleCancel = (skillId: string) => {
    const originalSkill = originalSkills.find((s) => s.id === skillId);
    if (originalSkill) {
      const skillIndex = editableSkills.findIndex((s) => s.id === skillId);
      if (skillIndex !== -1) {
        const updated = [...editableSkills];
        updated[skillIndex] = { ...originalSkill };
        setEditableSkills(updated);
      }
    }
    setEditingSkillId(null);
  };

  const handleSave = (index: number) => {
    const skill = editableSkills[index];
    if (!skill.id) {
      return;
    }

    updateSkill.mutate(
      {
        id: skill.id,
        payload: { isPublic: skill.isPublic, name: skill.name },
      },
      {
        onSuccess: () => {
          const updatedOriginals = [...originalSkills];
          const originalIndex = updatedOriginals.findIndex(
            (s) => s.id === skill.id
          );
          if (originalIndex !== -1) {
            updatedOriginals[originalIndex] = { ...skill };
            setOriginalSkills(updatedOriginals);
          }
          setEditingSkillId(null);
        },
      }
    );
  };

  const handleCreate = () => {
    if (!newSkillName.trim()) {
      return;
    }
    createSkill.mutate(
      {
        isPublic: newSkillIsPublic,
        name: newSkillName.trim(),
      },
      {
        onSuccess: () => {
          setNewSkillName('');
          setNewSkillIsPublic(true);
        },
      }
    );
  };

  // const handleDelete = (skillId: string) => {
  //   if (window.confirm('Are you sure you want to delete this skill?')) {
  //     deleteSkill.mutate(skillId);
  //   }
  // };

  if (getSkills.isError) {
    return (
      <Alert
        severity="error"
        sx={{
          '& .MuiAlert-icon': {
            fontSize: '1.5rem',
          },
          borderRadius: 2,
          mt: 2,
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          Failed to load skills
        </Typography>
        <Typography variant="body2">
          Please check your connection and try again.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 800 }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          fontWeight="bold"
          gutterBottom
          sx={{
            WebkitBackgroundClip: 'text',
            background: '#000000',
            backgroundClip: 'text',
            color: 'transparent',
            mb: 1,
          }}
        >
          Skills Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage skills that can be assigned to experts and used in job postings
        </Typography>
      </Box>

      <Card
        elevation={0}
        sx={{
          '&:hover': {
            backgroundColor: '#f8f9ff',
            borderColor: '#1976d2',
          },
          border: '2px dashed #e0e0e0',
          borderRadius: 3,
          mb: 4,
          transition: 'all 0.3s ease',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
            <Add sx={{ mr: 1, verticalAlign: 'middle' }} />
            Add New Skill
          </Typography>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <TextField
              label="Skill Name"
              variant="outlined"
              size="medium"
              value={newSkillName}
              onChange={(e) => setNewSkillName(e.target.value)}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
                maxWidth: 350,
              }}
              placeholder="e.g. React, JavaScript, UI/UX Design"
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreate}
              disabled={createSkill.isPending || !newSkillName.trim()}
              sx={{
                borderRadius: 2,
                fontSize: '1rem',
                minWidth: 120,
                px: 3,
                py: 1.5,
                textTransform: 'none',
              }}
            >
              {createSkill.isPending ? 'Adding...' : 'Add Skill'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {getSkills.isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={40} />
        </Box>
      ) : (
        <Box>
          <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
            Existing Skills ({editableSkills.length})
          </Typography>

          {editableSkills.length === 0 ? (
            <Card
              elevation={0}
              sx={{ backgroundColor: '#f8f9fa', py: 6, textAlign: 'center' }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No skills found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add your first skill using the form above
              </Typography>
            </Card>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {editableSkills.map((skill, index) => (
                <Fade in={true} key={skill.id} timeout={300}>
                  <Card
                    elevation={editingSkillId === skill.id ? 4 : 1}
                    sx={{
                      '&:hover': {
                        elevation: 3,
                        transform: 'translateY(-2px)',
                      },
                      border:
                        editingSkillId === skill.id
                          ? '2px solid #1976d2'
                          : '1px solid transparent',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Box flex={1}>
                          {editingSkillId === skill.id ? (
                            <Box display="flex" flexDirection="column" gap={2}>
                              <TextField
                                label="Skill Name"
                                variant="outlined"
                                size="small"
                                value={skill.name}
                                fullWidth
                                onChange={(e) =>
                                  handleSkillChange(
                                    index,
                                    'name',
                                    e.target.value
                                  )
                                }
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                  },
                                }}
                              />
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={skill.isPublic}
                                    onChange={(e) =>
                                      handleSkillChange(
                                        index,
                                        'isPublic',
                                        e.target.checked
                                      )
                                    }
                                    color="primary"
                                  />
                                }
                                label={
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    {skill.isPublic ? (
                                      <Visibility fontSize="small" />
                                    ) : (
                                      <VisibilityOff fontSize="small" />
                                    )}
                                    <Typography variant="body2">
                                      {skill.isPublic
                                        ? 'Public - Visible to all users'
                                        : 'Private - Admin only'}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </Box>
                          ) : (
                            <Box display="flex" alignItems="center" gap={2}>
                              <Typography variant="h6" fontWeight="500">
                                {skill.name}
                              </Typography>
                              <Chip
                                icon={
                                  skill.isPublic ? (
                                    <Visibility fontSize="small" />
                                  ) : (
                                    <VisibilityOff fontSize="small" />
                                  )
                                }
                                label={skill.isPublic ? 'Public' : 'Private'}
                                size="small"
                                variant="outlined"
                                color={skill.isPublic ? 'success' : 'default'}
                                sx={{
                                  backgroundColor: skill.isPublic
                                    ? '#e8f5e8'
                                    : '#fff3e0',
                                  fontWeight: 500,
                                }}
                              />
                            </Box>
                          )}
                        </Box>

                        <Box display="flex" gap={1}>
                          {editingSkillId === skill.id ? (
                            <>
                              <Tooltip title="Save changes">
                                <IconButton
                                  onClick={() => handleSave(index)}
                                  color="primary"
                                  disabled={updateSkill.isPending}
                                  sx={{
                                    '&:hover': { backgroundColor: '#bbdefb' },
                                    backgroundColor: '#e3f2fd',
                                  }}
                                >
                                  <Save />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel editing">
                                <IconButton
                                  onClick={() => handleCancel(skill.id)}
                                  color="secondary"
                                  sx={{
                                    '&:hover': { backgroundColor: '#f8bbd9' },
                                    backgroundColor: '#fce4ec',
                                  }}
                                >
                                  <Cancel />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              <Tooltip title="Edit skill">
                                <IconButton
                                  onClick={() => handleEdit(skill.id)}
                                  color="primary"
                                  disabled={editingSkillId !== null}
                                >
                                  <Edit />
                                </IconButton>
                              </Tooltip>
                              {/* <Tooltip title="Delete skill">
                                <IconButton
                                  onClick={() => handleDelete(skill.id)}
                                  color="error"
                                  disabled={editingSkillId !== null}
                                >
                                  <Delete />
                                </IconButton>
                              </Tooltip> */}
                            </>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SkillsSettings;
